import {
  defaultResourceListPreferences,
  type SavePreferencesFn,
} from '@fabric-app/business/resources/components/ResourceList/contexts/defaultPreferences';
import { getResourceListPreferences } from '@fabric-app/business/resources/components/ResourceList/getResourceListPreferences';
import { mergeListPreferences } from '@fabric-app/business/resources/utils/mergeListPreferences';
import { useMutationUserPreferencesUpdate } from '@fabric-app/business/user/mutations/useMutationUserPreferencesUpdate';
import { useQueryUserPreferences } from '@fabric-app/business/user/queries/useQueryUserPreferences';
import type { ResourceListPreferences } from '@fabric-app/types/resources/resourceListViewPreferences';
import { useCallback, useMemo, useState } from 'react';

export const useViewPreferences = () => {
  const { data: userPreferences, isLoading } = useQueryUserPreferences();

  /**
   * synced preferences
   */
  const { mutate: mutatePreferences } = useMutationUserPreferencesUpdate();
  const syncedPreferences = useMemo(
    () =>
      getResourceListPreferences(userPreferences?.frontend.viewSearchPreferences) ||
      defaultResourceListPreferences,
    [userPreferences],
  );
  const saveSyncedPreferences: SavePreferencesFn = useCallback(
    (data) => {
      mutatePreferences({
        data: {
          frontend: {
            viewSearchPreferences: data,
          },
        },
        showToast: false,
      });
    },
    [mutatePreferences],
  );

  /**
   * local settings, if user is not authenticated, we use local state to store the preferences
   */

  const [localPreferences, setLocalState] = useState<ResourceListPreferences>(
    defaultResourceListPreferences,
  );

  const saveLocalPreferences: SavePreferencesFn = useCallback((data) => {
    setLocalState((state) => {
      return mergeListPreferences(state, data);
    });
  }, []);

  if (userPreferences?.frontend.viewSearchPreferences) {
    return {
      isLoading,
      saveViewPreferences: saveSyncedPreferences,
      viewPreferences: syncedPreferences,
    };
  }

  return {
    isLoading,
    saveViewPreferences: saveLocalPreferences,
    viewPreferences: localPreferences,
  };
};
